import NewLinkDialog from "./NewLinkDialog";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Button, Card, Grid, Stack, Typography } from "@mui/material";
import Component from "components/Component";
import IconButton from "components/common/IconButton";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as programActions from "store/actions/input/program/programActions";

const getHostname = (url) => {
  try {
    const urlWithProtocol = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(urlWithProtocol).hostname?.replace(/^www\./, "");
  } catch (e) {
    // If parsing fails, manually strip protocol and www
    return url
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split("/")[0];
  }
};

const LinkRow = ({ title, url, onEdit, onDelete, onOpen, readOnly }) => {
  const [hovered, setHovered] = useState(false);
  const hostname = getHostname(url);
  return (
    <Card
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ cursor: "pointer", ":hover": { background: "#f5f7f9" } }}
      variant={"outlined"}
    >
      <Grid container alignItems={"center"} sx={{ padding: "10px 12px" }}>
        <Grid item xs>
          <Typography variant={"subtitle1"}>{title || hostname}</Typography>
          <Typography variant={"caption"} color={"text.secondary"}>
            {hostname}
          </Typography>
        </Grid>
        {hovered && !readOnly && (
          <Grid item xs={"auto"}>
            <IconButton
              variant={"bright"}
              icon={EditIcon}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            />
            <IconButton
              variant={"bright"}
              icon={DeleteIcon}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            />
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

const Links = ({ readOnly = false }) => {
  const dispatch = useDispatch();
  const links = useSelector((state) => state.input?.program?.links ?? []);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const openAdd = () => {
    if (readOnly) {
      return;
    }
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const openEdit = (index) => {
    if (readOnly) {
      return;
    }
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const saveLink = ({ url, title, description }) => {
    if (readOnly) {
      return;
    }
    const newItem = {
      url: url?.trim(),
      title: title?.trim() || null,
      description: description?.trim() || null,
    };
    let next = [...links];
    if (editingIndex == null) {
      next = [...next, newItem];
    } else {
      next[editingIndex] = newItem;
    }
    dispatch(programActions.updateProgram({ links: next }));
    setDialogOpen(false);
  };

  const deleteLink = (index) => {
    if (readOnly) {
      return;
    }
    const next = links.filter((_, i) => i !== index);
    dispatch(programActions.updateProgram({ links: next }));
  };

  const openUrl = (url) => {
    try {
      const candidate = /^(https?:)\/\//i.test(url) ? url : `https://${url}`;
      const parsed = new URL(candidate);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") {
        window.open(candidate, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      // ignore invalid url
    }
  };

  return (
    <Component
      title={"Links"}
      options={
        readOnly ? null : (
          <Button
            startIcon={<AddIcon />}
            onClick={openAdd}
            variant={"contained"}
            disableElevation
          >
            {"Add"}
          </Button>
        )
      }
    >
      {links.length === 0 ? (
        <Stack
          alignItems={"center"}
          sx={{
            color: "text.secondary",
            minHeight: "10rem",
            justifyContent: "center",
          }}
        >
          <Typography align={"center"} sx={{ maxWidth: "44ch" }}>
            {
              "Click the plus button to add external links related to this submission"
            }
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {links.map((link, index) => (
            <LinkRow
              key={`${link.url}-${index}`}
              title={link.title}
              url={link.url}
              onEdit={() => openEdit(index)}
              onDelete={() => deleteLink(index)}
              onOpen={() => openUrl(link.url)}
              readOnly={readOnly}
            />
          ))}
        </Stack>
      )}

      <NewLinkDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSave={saveLink}
        initialValue={editingIndex != null ? links[editingIndex] : null}
      />
    </Component>
  );
};

export default Links;
